/**
 * Nutrition table (Nährwertetabelle) column selector.
 *
 * Wires up the flavor/column dropdown for every `.nutritional-table`.
 * product.js also initialises these tables from the `<variant-selects>` /
 * `<product-form>` custom elements, but those elements are absent on bundle
 * product pages (default-only parent variant + bundle builder instead of the
 * product form). This script runs independently so the slider also works there.
 *
 * Initialisation is idempotent: every table is flagged with
 * `data-info-table-init` once bound, so this script and product.js never wire
 * up the same table twice.
 */
(function () {
  function enhanceDropdown(select) {
    if (!select || select.dataset.customDropdownInit === 'true') return;
    select.dataset.customDropdownInit = 'true';
    select.classList.add('nutritional-table-native-select');

    var field = select.closest('.nutritional-table-selector__field');
    field?.querySelector(':scope > span')?.setAttribute('hidden', '');

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'qlf-select-variant nutritional-table-custom-trigger';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    button.innerHTML = '<span class="qlf-select-variant-data"></span>';

    var list = document.createElement('ul');
    list.className = 'qlf-select-variant-list nutritional-table-custom-list';
    list.setAttribute('role', 'listbox');

    Array.from(select.options).forEach(function (option) {
      var item = document.createElement('li');
      item.className = 'nutritional-table-custom-option';
      item.setAttribute('role', 'option');
      item.dataset.value = option.value;
      item.innerHTML = '<span class="qlf-select-variant-list-data"></span>';
      item.querySelector('span').textContent = option.textContent;
      item.addEventListener('click', function () {
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        closeList();
      });
      list.appendChild(item);
    });

    var sync = function () {
      button.querySelector('.qlf-select-variant-data').textContent = select.selectedOptions[0]?.textContent || '';
      list.querySelectorAll('li').forEach(function (item) {
        var selected = item.dataset.value === select.value;
        item.classList.toggle('qlf-selected', selected);
        item.setAttribute('aria-selected', selected ? 'true' : 'false');
      });
    };
    var closeList = function () {
      list.classList.remove('visible');
      button.setAttribute('aria-expanded', 'false');
      sync();
    };

    button.addEventListener('click', function () {
      var willOpen = !list.classList.contains('visible');
      document.querySelectorAll('.nutritional-table-custom-list.visible').forEach(function (openList) {
        openList.classList.remove('visible');
      });
      list.classList.toggle('visible', willOpen);
      button.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    });
    select.addEventListener('change', sync);
    document.addEventListener('click', function (event) {
      if (!field?.contains(event.target)) closeList();
    });
    field?.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        closeList();
        button.focus();
      }
    });

    field?.append(button, list);
    sync();
  }

  function initBundleProductSelectors(scope) {
    scope.querySelectorAll('.bundle-product-info-tables__product-select').forEach(function (select) {
      if (select.dataset.bundleProductInit === 'true') return;
      select.dataset.bundleProductInit = 'true';
      enhanceDropdown(select);

      var bundleTables = select.closest('.bundle-product-info-tables');
      var updateProduct = function () {
        bundleTables?.querySelectorAll('[data-bundle-info-product]').forEach(function (item) {
          item.hidden = item.dataset.bundleInfoProduct !== select.value;
        });
      };

      select.addEventListener('change', updateProduct);
      updateProduct();
    });
  }

  function initInfoTables(root) {
    var scope = root instanceof HTMLElement ? root : document;
    initBundleProductSelectors(scope);

    scope.querySelectorAll('.nutritional-table').forEach(function (table) {
      if (table.dataset.infoTableDropdownInit === 'true') return;
      table.dataset.infoTableDropdownInit = 'true';
      table.dataset.infoTableInit = 'true';

      var currentColumn = 1;
      var rows = table.querySelectorAll('tr');
      if (rows.length === 0) return;
      var totalColumns = rows[0].querySelectorAll('th').length;

      // Find truly static columns vs paired columns
      var firstRowCells = rows[0].querySelectorAll('th');
      var staticColumnIndices = [];
      var pairedColumnIndices = [];

      firstRowCells.forEach(function (th, index) {
        if (th.classList.contains('table-cell')) {
          // Check if this is a flavor-specific %NRV column (at the end, multiple)
          if (index > 10 && th.textContent.includes('%NRV')) {
            pairedColumnIndices.push(index);
          } else {
            // Truly static column (always visible)
            staticColumnIndices.push(index);
          }
        }
      });

      // Calculate scrollable columns (excluding static and paired)
      // Helper function to get the paired column index
      var getPairedColumn = function (column) {
        if (pairedColumnIndices.length === 0) return null;

        // Map scrollable columns to paired columns
        // Columns 1-5 map to paired 0-4, columns 6-10 map to paired 0-4
        var scrollableIndex = column - 1;
        var pairedIndex = scrollableIndex % pairedColumnIndices.length;
        return pairedColumnIndices[pairedIndex];
      };

      var updateTable = function (column) {
        var pairedColumn = getPairedColumn(column);

        rows.forEach(function (row) {
          var cells = row.querySelectorAll('th, td');
          cells.forEach(function (cell, index) {
            // Show: static columns, current column, and paired column (if exists)
            if (staticColumnIndices.includes(index) || index === column || index === pairedColumn) {
              cell.style.setProperty('display', '', 'important');
            } else {
              cell.style.setProperty('display', 'none', 'important');
            }
          });
        });
      };

      var infoTableGroup = table.closest('[id^="qlf-product-info-table-"]');
      var selectorBlock = infoTableGroup && infoTableGroup.querySelector('.nutritional-table-selector');
      if (selectorBlock && selectorBlock.parentElement !== infoTableGroup) {
        infoTableGroup.insertBefore(selectorBlock, infoTableGroup.firstElementChild);
      }
      var flavorSelect = infoTableGroup && infoTableGroup.querySelector('.nutritional-table-flavor-select');
      var productFlavorSelect = infoTableGroup && infoTableGroup.querySelector('.nutritional-table-product-flavor-select');
      var measureSelect = infoTableGroup && infoTableGroup.querySelector('.nutritional-table-measure-select');
      var normalColumns = [];
      var lastColumns = [];

      firstRowCells.forEach(function (th, index) {
        if (th.classList.contains('nutrition-column--normal')) normalColumns.push(index);
        if (th.classList.contains('nutrition-column--last')) lastColumns.push(index);
      });

      var selectedColumn = function () {
        var flavorIndex = flavorSelect ? Number(flavorSelect.value) : 0;
        var columns = measureSelect && measureSelect.value === 'last' ? lastColumns : normalColumns;
        return columns[Math.min(flavorIndex, columns.length - 1)] || normalColumns[0] || 1;
      };

      var updateFromSelects = function () {
        currentColumn = selectedColumn();
        updateTable(currentColumn);
      };

      if (flavorSelect) {
        Array.from(flavorSelect.options).forEach(function (option) {
          var originalLabel = option.textContent.trim();
          var flavorInParentheses = originalLabel.match(/\(([^()]*)\)\s*$/u);
          option.textContent = flavorInParentheses ? flavorInParentheses[1].trim() : originalLabel;
        });
        flavorSelect.addEventListener('change', updateFromSelects);
      }
      enhanceDropdown(productFlavorSelect);
      enhanceDropdown(flavorSelect);
      enhanceDropdown(measureSelect);
      if (productFlavorSelect && productFlavorSelect.dataset.variantSyncInit !== 'true') {
        productFlavorSelect.dataset.variantSyncInit = 'true';
        var updateFlavor = function () {
          var selectedSku = productFlavorSelect.value.trim();
          infoTableGroup.querySelectorAll('.table-container[data-nutrition-sku]').forEach(function (container) {
            container.hidden = container.dataset.nutritionSku !== selectedSku;
          });
        };
        productFlavorSelect.addEventListener('change', updateFlavor);
        updateFlavor();
      }
      measureSelect?.addEventListener('change', updateFromSelects);

      updateFromSelects();
    });
  }

  window.qlfInitInfoTables = initInfoTables;

  // Initialise whatever is already in the DOM when this script runs. This
  // handles bundle pages (no variant-selects / product-form) as well as the
  // table being re-injected via quick view.
  initInfoTables(document);

  if (!window.__qlfInfoTableListeners) {
    window.__qlfInfoTableListeners = true;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        initInfoTables(document);
      });
    }

    // Re-bind tables that get rendered/replaced after the initial load.
    document.addEventListener('product:variant-rendered', function () {
      initInfoTables(document);
    });
    document.addEventListener('quick-view:open', function () {
      initInfoTables(document);
    });
    document.addEventListener('shopify:section:load', function () {
      initInfoTables(document);
    });
  }
})();
